import {XIcon, FileDiffIcon, CommentIcon} from '@primer/octicons-react'
import {IconButton, Label} from '@primer/react'
import {useCallback, memo, type MouseEvent} from 'react'
import type {SuggestedChangeLineRange, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {useUnifiedBatch, useUnifiedBatchActions} from '../../../stores/layout-selectors'
import type {UnifiedBatchItem} from '../../../stores/slices/unified-batch'
import {ZeroState} from '../ZeroState'
import {restoreFocusOnNextFrame} from '../panel-focus'
import styles from './ManageBatchCommentsList.module.css'

export const MANAGE_BATCH_COMMENTS_HEADING_ID = 'manage-batch-comments-heading'
const REMOVE_BATCH_ITEM_BUTTON_SELECTOR = '[data-remove-batch-item-button]'

/**
 * Shared `Comments(N)` list used by both Manage batch tabs.
 *
 * Each row is a one-line `path:start-end` summary with an `X` button that
 * removes the comment from the batch. Outdated rows render an "Outdated"
 * label (the rule that excludes outdated items from the commit path lives
 * in #801).
 */
export function ManageBatchCommentsList() {
  const {items} = useUnifiedBatch()
  const {removeItem} = useUnifiedBatchActions()

  if (items.length === 0) {
    return (
      <ZeroState
        heading="No comments in batch"
        description="Add a review comment to the batch from any comment's resolve row."
        icon={CommentIcon}
        headingLevel="h4"
      />
    )
  }

  return (
    <ul className={styles.list} data-testid="manage-batch-comments-list">
      {items.map(item => (
        <ManageBatchCommentRow key={rowKey(item)} item={item} onRemove={removeItem} />
      ))}
    </ul>
  )
}

const ManageBatchCommentRow = memo(function ManageBatchCommentRowImpl({
  item,
  onRemove,
}: {
  item: UnifiedBatchItem
  onRemove: (id: SuggestionIdentifier) => void
}) {
  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const focusTarget = getFocusTargetAfterBatchItemRemoval(event.currentTarget)

      onRemove({commentDatabaseId: item.commentDatabaseId, suggestionIndex: item.suggestionIndex})
      restoreFocusOnNextFrame(focusTarget)
    },
    [item.commentDatabaseId, item.suggestionIndex, onRemove],
  )

  const rangeSummary = formatRowRangeSummary(item)
  const rowLabel = rangeSummary ? `${item.path}:${rangeSummary}` : item.path
  const testId = `manage-batch-row-${item.commentDatabaseId}-${item.suggestionIndex ?? 0}`

  return (
    <li className={styles.row} data-testid={testId}>
      <FileDiffIcon className="fgColor-muted flex-shrink-0" />
      <span className={styles.path}>
        <span className={styles.pathText}>{item.path}</span>
        {rangeSummary && <span className="fgColor-muted">{`:${rangeSummary}`}</span>}
      </span>
      {item.isOutdated && (
        <Label variant="attention" size="small">
          Outdated
        </Label>
      )}
      <IconButton
        icon={XIcon}
        aria-label={item.isOutdated ? `Remove ${rowLabel} from batch (outdated)` : `Remove ${rowLabel} from batch`}
        variant="invisible"
        size="small"
        data-remove-batch-item-button
        onClick={handleRemove}
      />
    </li>
  )
})

function rowKey(item: UnifiedBatchItem): string {
  return `${item.commentDatabaseId}:${item.suggestionIndex ?? 0}`
}

function getFocusTargetAfterBatchItemRemoval(button: HTMLButtonElement) {
  const row = button.closest('li')
  const nextRemoveButton = row?.nextElementSibling?.querySelector<HTMLButtonElement>(REMOVE_BATCH_ITEM_BUTTON_SELECTOR)
  const previousRemoveButton = row?.previousElementSibling?.querySelector<HTMLButtonElement>(
    REMOVE_BATCH_ITEM_BUTTON_SELECTOR,
  )
  return nextRemoveButton ?? previousRemoveButton ?? document.getElementById(MANAGE_BATCH_COMMENTS_HEADING_ID)
}

/**
 * Builds the trailing range summary shown after the path on each row, e.g.
 * `10-12`, `10`, `10-12, 20-25`. CCR suggestions that span multiple hunks
 * carry their ranges in `pathLineRanges` with `lineRange: undefined`
 * (see {@link UnifiedBatchItem}), so we prefer that source when present and
 * fall back to the single `lineRange`. Items without any range (e.g.
 * `human-without-suggestion`) return `''` and the row renders the path alone.
 */
function formatRowRangeSummary(item: UnifiedBatchItem): string {
  const ranges = collectRanges(item)
  if (ranges.length === 0) return ''
  return ranges.map(formatLineRange).join(', ')
}

function collectRanges(item: UnifiedBatchItem): SuggestedChangeLineRange[] {
  if (item.pathLineRanges && item.pathLineRanges.length > 0) {
    return item.pathLineRanges.map(entry => entry.lineRange)
  }
  return item.lineRange ? [item.lineRange] : []
}

function formatLineRange(range: SuggestedChangeLineRange): string {
  return range.startLineNumber === range.endLineNumber
    ? `${range.startLineNumber}`
    : `${range.startLineNumber}-${range.endLineNumber}`
}
